/**
 * Utilities for sharing content across various platforms
 */

export interface ShareContent {
  title: string;
  text: string;
  url: string;
}

/**
 * Check if the Web Share API is supported by the browser
 */
export function isWebShareSupported(): boolean {
  return typeof navigator !== 'undefined' && 'share' in navigator;
}

/**
 * Share content using the native Web Share API
 * @throws Error if Web Share API is not supported or share fails
 */
export async function shareViaWebApi(content: ShareContent): Promise<void> {
  if (!isWebShareSupported()) {
    throw new Error('Web Share API is not supported in this browser');
  }

  try {
    await navigator.share({
      title: content.title,
      text: content.text,
      url: content.url,
    });
  } catch (error) {
    // User cancelled the share or an error occurred
    if (error instanceof Error && error.name !== 'AbortError') {
      throw error;
    }
  }
}

/**
 * Generate email share URL
 */
export function getEmailShareUrl(content: ShareContent): string {
  const subject = encodeURIComponent(content.title);
  const body = encodeURIComponent(`${content.text}\n\n${content.url}`);
  return `mailto:?subject=${subject}&body=${body}`;
}

/**
 * Generate WhatsApp share URL
 */
export function getWhatsAppShareUrl(content: ShareContent): string {
  const text = encodeURIComponent(
    `${content.title}\n${content.text}\n${content.url}`,
  );
  return `https://wa.me/?text=${text}`;
}

/**
 * Generate Telegram share URL
 */
export function getTelegramShareUrl(content: ShareContent): string {
  const text = encodeURIComponent(`${content.title}\n${content.text}`);
  const url = encodeURIComponent(content.url);
  return `https://t.me/share/url?url=${url}&text=${text}`;
}

/**
 * Share methods available
 */
export type ShareMethod = 'webapi' | 'email' | 'whatsapp' | 'telegram' | 'copy';

export interface ShareMethodConfig {
  id: ShareMethod;
  label: string;
  icon: string;
  available: boolean;
  action: (content: ShareContent) => Promise<void> | void;
}

/**
 * Get all available share methods with their configurations
 */
export function getShareMethods(): ShareMethodConfig[] {
  const methods: ShareMethodConfig[] = [
    {
      id: 'webapi',
      label: 'Share...',
      icon: 'Share2',
      available: isWebShareSupported(),
      action: async (content) => {
        await shareViaWebApi(content);
      },
    },
    {
      id: 'email',
      label: 'Email',
      icon: 'Mail',
      available: true,
      action: (content) => {
        window.location.href = getEmailShareUrl(content);
      },
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: 'MessageCircle',
      available: true,
      action: (content) => {
        window.open(
          getWhatsAppShareUrl(content),
          '_blank',
          'noopener,noreferrer',
        );
      },
    },
    {
      id: 'telegram',
      label: 'Telegram',
      icon: 'Send',
      available: true,
      action: (content) => {
        window.open(
          getTelegramShareUrl(content),
          '_blank',
          'noopener,noreferrer',
        );
      },
    },
    {
      id: 'copy',
      label: 'Copy link',
      icon: 'Copy',
      available: true,
      action: async (content) => {
        await navigator.clipboard.writeText(content.url);
      },
    },
  ];

  return methods.filter((method) => method.available);
}
